import { useMemo, useState } from "react";
import { Check, Copy, Download, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock, langForFilename } from "@/components/CodeBlock";
import { FileTree } from "@/components/FileTree";
import { RuneLogo } from "@/components/RuneLogo";

export const SkillPreviewPane = ({ skill, generating = false, onDownload = null, locked = false, downloadLabel = "Download .zip" }) => {
  const [active, setActive] = useState("SKILL.md");
  const [copied, setCopied] = useState(false);

  const files = useMemo(() => skill?.files || [], [skill]);
  const activeFile = useMemo(() => {
    if (active === "SKILL.md") return { filename: "SKILL.md", content: skill?.skill_md || "" };
    return files.find((f) => f.filename === active) || { filename: active, content: "" };
  }, [active, skill, files]);

  const activeLocked = locked && active !== "SKILL.md";
  const skillmdLocked = locked && active === "SKILL.md";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(activeFile.content || "");
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  if (!skill) {
    return (
      <div className="rb-grain relative flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-8 text-center">
        <div className="mb-4 opacity-80"><RuneLogo className="h-10 w-10" /></div>
        <h3 className="rb-display text-lg font-semibold text-[color:var(--rb-text)]">Your skill will appear here</h3>
        <p className="mt-2 max-w-xs text-sm text-[color:var(--rb-text-faint)]">
          Describe what you want in the chat. The agent will craft a valid <span className="rb-mono text-[color:var(--rb-accent)]">SKILL.md</span> and any helper files, previewed live.
        </p>
      </div>
    );
  }

  return (
    <div className="grid h-full grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
      <div className="flex items-center justify-between gap-3 border-b border-[color:var(--rb-border)] px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="rb-mono truncate text-sm text-[color:var(--rb-text)]" data-testid="preview-skill-slug">{skill.slug}/</span>
            {skill.category && <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{skill.category}</Badge>}
          </div>
          {skill.short_description && <p className="mt-0.5 truncate text-xs text-[color:var(--rb-text-faint)]">{skill.short_description}</p>}
        </div>
        {onDownload && (
          <Button size="sm" onClick={onDownload} data-testid="buy-download-button"
            className="shrink-0 rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            <Download className="mr-1.5 h-3.5 w-3.5" /> {downloadLabel}
          </Button>
        )}
      </div>

      <Tabs value={active === "SKILL.md" ? "skillmd" : "files"} className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)]">
        <div className="flex items-center justify-between border-b border-[color:var(--rb-border)] px-3">
          <TabsList className="h-10 bg-transparent p-0">
            <TabsTrigger value="skillmd" onClick={() => setActive("SKILL.md")}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--rb-accent)] data-[state=active]:shadow-none text-[color:var(--rb-text-faint)]">
              <FileText className="mr-1.5 h-3.5 w-3.5" /> SKILL.md
            </TabsTrigger>
            <TabsTrigger value="files" onClick={() => setActive(files[0]?.filename || "SKILL.md")}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[color:var(--rb-accent)] data-[state=active]:shadow-none text-[color:var(--rb-text-faint)]">
              Files <span className="ml-1.5 rounded-full bg-white/5 px-1.5 text-[10px]">{files.length}</span>
            </TabsTrigger>
          </TabsList>
          <Button variant="ghost" size="sm" onClick={copy} disabled={locked}
            className="h-8 text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]" data-testid="copy-skillmd-button">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {/* SKILL.md tab */}
        <TabsContent value="skillmd" className="m-0 min-h-0 flex-1 overflow-auto">
          {generating && !skill.skill_md ? (
            <div className="p-6 text-sm text-[color:var(--rb-text-faint)]">Generating SKILL.md…</div>
          ) : skillmdLocked ? (
            <div className="relative" data-testid="skillmd-locked">
              <CodeBlock code={skill.skill_md} language="markdown" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-[color:var(--rb-obsidian)] via-[color:var(--rb-obsidian)] to-transparent" />
              <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 px-4 pb-5 text-center">
                <Lock className="h-5 w-5 text-[color:var(--rb-gold)]" />
                <p className="max-w-xs text-xs text-[color:var(--rb-text-faint)]">
                  Showing the first {(skill.skill_md || "").split("\n").length} of {skill.skill_md_total_lines || "?"} lines.
                  Unlock the full SKILL.md{files.length ? ` + ${files.length} file(s)` : ""}.
                </p>
                {onDownload && (
                  <Button size="sm" onClick={onDownload} data-testid="unlock-skill-button"
                    className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                    <Lock className="mr-1.5 h-3.5 w-3.5" /> {downloadLabel}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <CodeBlock code={skill.skill_md} language="markdown" />
          )}
        </TabsContent>

        {/* Files tab */}
        <TabsContent value="files" className="m-0 grid min-h-0 flex-1 grid-cols-[180px_1fr] overflow-hidden">
          <div className="overflow-auto border-r border-[color:var(--rb-border)] p-2">
            <FileTree files={files} activePath={activeFile.filename} onSelect={setActive} locked={locked} />
          </div>
          <div className="relative overflow-auto">
            {activeLocked ? (
              <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 p-6 text-center">
                <Lock className="h-6 w-6 text-[color:var(--rb-gold)]" />
                <p className="max-w-xs text-sm text-[color:var(--rb-text-faint)]">This file is locked. Unlock the full skill to view &amp; download every file.</p>
                {onDownload && (
                  <Button size="sm" onClick={onDownload} data-testid="unlock-file-button"
                    className="rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                    <Lock className="mr-1.5 h-3.5 w-3.5" /> {downloadLabel}
                  </Button>
                )}
              </div>
            ) : (
              <CodeBlock code={activeFile.content} language={langForFilename(activeFile.filename)} />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
